import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { UserCircle2, MapPinned, CalendarDays, LoaderCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSelector from "@/components/LanguageSelector";

export default function EmployeeProfilePage() {
    const { user } = useAuth();
    const { t } = useTranslation();
    const nav = useNavigate();
    const [home, setHome] = useState(null);

    useEffect(() => {
        api.get("/employee/home").then((r) => setHome(r.data)).catch(() => setHome(null));
    }, []);

    const employee = home?.employee || null;

    return (
        <div className="space-y-4" data-testid="employee-profile-page">
            <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">{t("profile.title")}</div>
                <h2 className="text-2xl font-bold mt-1">{user?.name || employee?.name || t("profile.employee")}</h2>
            </div>

            <Card className="p-4 space-y-3" data-testid="employee-language-settings">
                <div>
                    <div className="font-semibold">{t("settings.language_title")}</div>
                    <div className="text-xs text-muted-foreground">{t("settings.language_help")}</div>
                </div>
                <LanguageSelector testId="employee-language-selector" />
            </Card>

            <Card className="p-4 space-y-3">
                <ProfileRow label={t("profile.role")} value={user?.role_name || t("common.no_data")} />
                <ProfileRow label={t("profile.email")} value={user?.email || t("common.no_data")} />
                <ProfileRow label={t("profile.position")} value={employee?.position || t("common.not_assigned")} />
                <ProfileRow label={t("profile.department")} value={employee?.department || t("common.not_assigned")} />
                <ProfileRow label={t("profile.assigned_area")} value={home?.assigned_area || t("common.not_assigned")} />
                <ProfileRow label={t("profile.shift_status")} value={home?.shift_status || t("common.no_data")} />
            </Card>

            <Card className="p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold">
                    <CalendarDays className="w-4 h-4 text-primary" />{t("common.today")}
                </div>
                <ProfileRow label={t("common.date")} value={home?.date || t("common.no_data")} />
                <ProfileRow label={t("profile.today_tasks")} value={String(home?.today_task_count ?? 0)} />
            </Card>

            <Card className="p-4 text-sm text-muted-foreground">
                {t("profile.salary_private")}
            </Card>

            <Card className="p-4">
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <div className="text-sm font-semibold">{t("profile.my_attendance")}</div>
                        <div className="text-xs text-muted-foreground">{t("profile.attendance_help")}</div>
                    </div>
                    <Button className="h-10" onClick={() => nav("/field/attendance")}>{t("common.open")}</Button>
                </div>
            </Card>

            <Card className="p-4">
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <div className="text-sm font-semibold">{t("profile.my_payroll")}</div>
                        <div className="text-xs text-muted-foreground">{t("profile.payroll_help")}</div>
                    </div>
                    <Button className="h-10" onClick={() => nav("/field/payroll")}>{t("common.open")}</Button>
                </div>
            </Card>
        </div>
    );
}

function ProfileRow({ label, value }) {
    return (
        <div className="flex items-start justify-between gap-3 border-b border-border/60 pb-2 last:border-b-0 last:pb-0">
            <div className="text-sm text-muted-foreground">{label}</div>
            <div className="text-sm font-medium text-right max-w-[60%]">{value || "No data"}</div>
        </div>
    );
}
